import type { AcceptTermsResult, TermsView } from '@assessments/shared'
import { apiFetch } from '../../services/apiClient.js'
import { ApiError, assertOk } from '../../services/apiError.js'

/** Thrown when the server reports the accepted version is no longer applicable (409). */
export class TermsChangedError extends Error {
  constructor() {
    super('The terms have changed. Please review and accept the new version.')
    this.name = 'TermsChangedError'
  }
}

export async function getTerms(organizationSurveyId: string): Promise<TermsView> {
  const res = await apiFetch(`/assessments/${encodeURIComponent(organizationSurveyId)}/terms`)
  if (!res.ok) {
    throw new Error(`Failed to load terms (${res.status})`)
  }
  return (await res.json()) as TermsView
}

/**
 * Records the org's acceptance of `version`. Throws {@link TermsChangedError} on a 409,
 * and an error carrying the server's reason on a 403.
 */
export async function postAcceptance(
  organizationSurveyId: string,
  version: number,
): Promise<AcceptTermsResult> {
  const res = await apiFetch(
    `/assessments/${encodeURIComponent(organizationSurveyId)}/terms/acceptance`,
    { method: 'POST', body: JSON.stringify({ version }) },
  )
  if (res.status === 409) {
    throw new TermsChangedError()
  }
  // A 403 explains why this user can't accept (e.g. no linked user record).
  if (res.status === 403) {
    const body = (await res.json().catch(() => undefined)) as { message?: string } | undefined
    throw new ApiError(403, body?.message ?? 'You can’t accept the Terms of Use.', body)
  }
  await assertOk(res, 'record acceptance')
  return (await res.json()) as AcceptTermsResult
}
